export type FiscalYear = {
    id: string;
    year: number;
}

export type PayPeriodFilterOptions = {
    page: number;
    pageSize: number;
    fiscalYearId: string;
}

export type PayPeriod = {
    id: string;
    fiscalYear: number;
    fiscalPeriodId: string;
    fiscalPeriodStartDate: Date;
    fiscalPeriodEndDate: Date;
    isCompleted: boolean;
    deadlineDate: Date;
    certifiedData: boolean;
    certifiedReports: boolean;
    certifiedPartiesNotified: boolean;
    certifiedByPersonId: string | null;
    certifiedByPersonName: string | null;
    certifiedDate: Date | null;
}

export type UpdatePayPeriod = {
    id: string;
    certifiedData: boolean;
    certifiedReports: boolean;
    certifiedPartiesNotified: boolean;
    certifiedByPersonId: string | null;
    certifiedDate: Date | null;
    isCompleted: boolean;
}

export type TTIAndDepartmentAccountsFilterOptions = {
    payPeriodStartDate: Date;
}

export type TTIAndDepartmentAccounts = {
    ttiAccounts: Array<TTIAccount>;
    departmentAccounts: Array<DepartmentAccount>;
}

export type TTIAccount = {
    sl: string | null;
    sa: string | null;
    gl: string | null;
    objectCode: string | null;
    departmentGL: string | null;
    departmentObjectCode: string | null;
    departmentSL: string | null;
    departmentSA: string | null;
    idcCredit:  number | null;
}

export type DepartmentAccount = {
    sl: string | null;
    sa: string | null;
    gl: string | null;
    pigl: string | null;
    pisl: string | null;
    pisa: string | null;
    piidcCredit:  number | null;
}

export type AdLocFilterOptions = {
    payPeriodStartDate: Date;
}

export type AdLocTTIAccounts = {
    ttiAccountSL: string | null;
    ttiAccountSA: string | null;
    departmentSL: string | null;
    departmentSA: string | null;
    amountPaid: number | null;
    idcCredit:  number | null;
    departmentIDCCredit: number | null;
    piidcCredit: number | null;
    rdfCredit: number | null;
}

export type AdLoc = {
    adLoc: string | null;
    departmentName: string | null;
    ttiAccountGL: string | null;
    ttiObjectCode: string | null;
    deparmentGL: string | null;
    departmentObjectCode: string | null;
    amountPaid: number | null;
    idcCredit: number | null;
    departmentIDCCredit: number | null;
    piidcCredit: number | null;
    rdfCredit: number | null;
    ttiAccounts: Array<AdLocTTIAccounts>;
}

export type ResponsiblePeopleFilterOptions = {
    payPeriodStartDate: Date;
    adLoc: string;
}

export type AdLocAccount = {
    sl: string | null;
    sa: string | null;
    piidcCredit: number | null;
    rdfCredit: number | null;
}

export type ResponsiblePerson = {
    responsiblePersonName: string | null;
    responsiblePersonUIN: string | null;
    pigl: string | null;
    pisl: string | null;
    pisa: string | null;
    piidcCredit: number | null;
    rdfCredit: number | null;
    adLocAccounts: Array<AdLocAccount>;
}

export type AccountPayeesFilterOptions = {
    payPeriodStartDate: Date;
    adLoc: string;
}

export type Payee = {
    payeeName: string | null;
    payeeUIN: string | null;
    payeeTitleLevel: string | null;
    payeeTitleCode: string | null;
    payeeTitle: string | null;
    payrollEntryDate: Date | null;
    amountPaid: number | null;
    idcCredit: number | null;
    departmentIDCCredit: number | null;
    piidcCredit: number | null;
    rdfCredit: number | null;
}

export type Account = {
    sl: string | null;
    sa: string | null;
    responsiblePersonName: string | null;
    responsiblePersonUIN: string | null;
    ttiDepartment: string | null;
    startDate: Date | null;
    distribution: string | null;
    indirectCostRate: number | null;
    amountPaid: number | null;
    idcCredit: number | null;
    departmentIDCCredit: number | null;
    piidcCredit: number | null;
    rdfCredit: number | null;
    payees: Array<Payee>;
}

export type UpdateAdLoc = {
    payPeriodStartDate: Date;
    previousAdLoc: string | null;
    previousDepartmentName: string | null;
    newAdLoc: string;
    newDepartmentName: string;
}

export type UpdateResponsiblePerson = {
    payPeriodStartDate: Date;
    adLoc: string | null;
    newResponsiblePersonName: string | null;
    newResponsiblePersonUIN: string | null;
    newPIGL: string | null;
    newPISL: string | null;
    newPISA: string | null;
    previousResponsiblePersonName: string | null;
    previousResponsiblePersonUIN: string | null;
    previousPIGL: string | null;
    previousPISL: string | null;
    previousPISA: string | null;
}

export type UpdatePayee = {
    payPeriodStartDate: Date;
    adLoc: string | null;
    sl: string | null;
    sa: string | null;
    responsiblePersonName: string | null;
    responsiblePersonUIN: string | null;
    ttiDepartment: string | null;
    startDate: Date | null;
    distribution: string | null;
    indirectCostRate: number | null;
    payrollEntryDate: Date | null;
    previousPayeeName: string | null;
    previousPayeeUIN: string | null;
    previousPayeeTitleLevel: string | null;
    previousPayeeTitleCode:  string | null;
    previousPayeeTitle: string | null;
    newPayeeName: string | null;
    newPayeeUIN: string | null;
    newPayeeTitleLevel: string | null;
    newPayeeTitleCode: string | null;
    newPayeeTitle: string | null;
}

export type UpdateTTIAndDepartmentAccounts = {
    payPeriodStartDate: Date;
    adLoc: string;
    departmentName: string;
    previousTTIAccountGL: string | null;
    previousTTIAccountSL: string | null;
    previousTTIAccountSA: string | null;
    previousDepartmentGL: string | null;
    previousDepartmentSL: string | null;
    previousDepartmentSA: string | null;
    newTTIAccountGL: string;
    newTTIAccountSL: string;
    newTTIAccountSA: string;
    newDepartmentGL: string;
    newDepartmentSL: string;
    newDepartmentSA: string;
}

export type AICAttachment = {
    id: string;
    fileName: string;
    createdByPersonName: string;
    createdDate: Date;
}

export type AICAttachments = {
    attachmentType: number;
    attachments: Array<AICAttachment>;
}

export enum AttachmentType {
    "Transfer Request Report" = 0,
    "Account Details Report" = 1,
    "Budget Entry Report" = 2
}